import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { allSites } from "../redux/slices/siteSlice";
import { useSocket } from "../contexts/SocketContext";

/**
 * Custom hook for managing site socket events and data fetching
 */
export const useSiteManagement = ({
  filters,
  page = 1,
  setSiteCreationStatus,
  setSiteCreation,
  showSuccess,
}) => {
  const { socket } = useSocket();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (!socket) return;

    const refreshSites = () => {
      dispatch(
        allSites({
          environment: filters.environment || "",
          status: filters.status || "",
          search: filters.search || "",
          includeDeleted: filters.showDeleted,
          page,
        }),
      );
    };

    const handleSiteCreationStatus = (data) => {
      // Update status in UI
      setSiteCreationStatus(data.status);

      // Refresh sites for any status change to reflect current state
      refreshSites();

      // Stop creation spinner only when site is fully active or failed
      if (data.status === "ACTIVE" || data.status === "FAILED") {
        setSiteCreation(false);
      }
    };

    const handleSiteDeleted = (data) => {
      if (data.userId === user?.id) {
        refreshSites();
        showSuccess(`Site "${data.siteName}" deleted successfully`);
      }
    };

    const handleSiteRestored = (data) => {
      if (data.userId === user?.id) {
        refreshSites();
        showSuccess(`Site "${data.siteName}" restored successfully`);
      }
    };

    // Listen to both event names for backward compatibility
    socket.on("site_status", handleSiteCreationStatus);
    socket.on("siteCreationProgress", handleSiteCreationStatus);
    socket.on("site_deleted", handleSiteDeleted);
    socket.on("site_soft_deleted", handleSiteDeleted);
    socket.on("site_restored", handleSiteRestored);

    return () => {
      socket.off("site_status", handleSiteCreationStatus);
      socket.off("siteCreationProgress", handleSiteCreationStatus);
      socket.off("site_deleted", handleSiteDeleted);
      socket.off("site_soft_deleted", handleSiteDeleted);
      socket.off("site_restored", handleSiteRestored);
    };
  }, [
    socket,
    user,
    dispatch,
    page,
    filters.environment,
    filters.status,
    filters.search,
    filters.showDeleted,
    setSiteCreationStatus,
    setSiteCreation,
    showSuccess,
  ]);

  // Fetch sites on filter or page change
  useEffect(() => {
    dispatch(
      allSites({
        environment: filters.environment || "",
        status: filters.status || "",
        search: filters.search || "",
        includeDeleted: filters.showDeleted,
        page,
      }),
    );
  }, [
    dispatch,
    filters.status,
    filters.environment,
    filters.search,
    filters.showDeleted,
    page,
  ]);
};
